/**
 * How a move aims (docs/systems/SKILLS_V2.md 1.1–1.2): ท่าล็อกเป้า (`lock`), ท่าทิศทาง (`free`), ท่าเลือกพื้นที่ (`ground`)
 * and ท่าระยะไกล (`volley`), the lock-on numbers, and the pure geometry the World and systems/lockOn.ts use.
 * Pure: no DOM, no timers, no randomness.
 */
import type { SkillDef } from './skills';

export type SkillAim = 'lock' | 'free' | 'ground' | 'volley';

export const AIM = {
  /** A key held at least this long locks (the client sends `hold: true`); shorter is a tap. */
  holdS: 0.2,
  /** A tap with no current target takes the nearest hostile within this cone ahead (full angle, degrees). */
  tapConeDeg: 60,
  /** The lock drops once the target is farther than range + this… */
  dropSlack: 4,
  /** …for this long (also when it dies or vanishes). */
  dropGrace: 0.4,
  /** A dropped lock leaves the move's cooldown at this share of the full cooldown. */
  dropCooldown: 0.6,
  /** A lock lasts at least this long after the move starts, plus the move's own wind-up, hits and warps. */
  formTail: 0.35,
  /** Soft-homing swords turn at most this many degrees a second. */
  homingTurnDeg: 90,
  /** A volley's projectile still hits a target within this of its path's end. */
  volleyReach: 0.9,
} as const;

/** The move's aim: its `aim`, else derived from its targeting fields (so no existing move needs an edit). */
export function aimOf(s: SkillDef): SkillAim {
  if (s.kind === 'break') return 'lock'; // a break must meet a guard
  if (s.aim) return s.aim;
  if (s.ground) return 'ground';
  if (s.volley) return 'volley';
  if (!s.aoe && !s.dash && !s.blink && s.range >= 4) return 'volley'; // a single long shot
  if (s.dash === 'forward' || s.spin || (s.aoe && !s.blink)) return 'free';
  return 'lock';
}

/** A volley's homing: its `homing`, else needles fly straight and everything else (flying swords) curves. */
export const homingOf = (s: SkillDef): 'soft' | 'none' => s.homing ?? (s.fx === 'needles' ? 'none' : 'soft');

/** How long a lock on this move lasts at least (its form): wind-up, the strikes 0.15 s apart, warps, a tail. */
export const lockFormS = (s: SkillDef): number =>
  s.windup +
  Math.max(0, (s.hits ?? 1) - 1) * 0.15 +
  (s.blink ? (s.hits ?? 1) * s.blink : 0) +
  (s.volley ? 0.65 + s.volley * 0.07 : 0) +
  AIM.formTail;

interface Pt {
  x: number;
  y: number;
}

/** Is `t` within the cone ahead of `e` (facing dirX, dirY), `deg` wide in all? */
export function inCone(e: Pt & { dirX: number; dirY: number }, t: Pt, deg: number = AIM.tapConeDeg): boolean {
  const dx = t.x - e.x;
  const dy = t.y - e.y;
  const d = Math.hypot(dx, dy);
  const f = Math.hypot(e.dirX, e.dirY);
  if (d < 1e-6 || f < 1e-6) return true;
  const cos = (dx * e.dirX + dy * e.dirY) / (d * f);
  return cos >= Math.cos(((deg / 2) * Math.PI) / 180);
}

/**
 * Where a ground move lands: the picked point, else the foe's feet, else straight ahead at full range; never farther
 * than `range` from the caster (the server re-clamps a point sent from afar).
 */
export function groundSpot(
  e: Pt & { dirX: number; dirY: number },
  range: number,
  at: Pt | null | undefined,
  foe: Pt | null,
): Pt {
  const aim = at ?? foe;
  const f = Math.hypot(e.dirX, e.dirY) || 1;
  const [dx, dy] = aim ? [aim.x - e.x, aim.y - e.y] : [(e.dirX / f) * range, (e.dirY / f) * range];
  const d = Math.hypot(dx, dy);
  const k = d > range ? range / d : 1;
  return { x: e.x + dx * k, y: e.y + dy * k };
}

/**
 * Does a volley projectile launched from `from` at the target's spot `aimed` still hit the target now at `now`,
 * `flightS` seconds later? Straight (`none`): the target must still be on the line through the aimed spot. Soft: the
 * sword may bend toward the target by at most `homingTurnDeg` a second, and reaches as far as the target went.
 */
export function volleyHits(homing: 'soft' | 'none', from: Pt, aimed: Pt, now: Pt, flightS: number): boolean {
  if (homing === 'none') {
    // Straight: the target must still be on the line (within reach of the ray through the aimed spot).
    const lx = aimed.x - from.x;
    const ly = aimed.y - from.y;
    const l = Math.hypot(lx, ly);
    if (l < 1e-6) return Math.hypot(now.x - from.x, now.y - from.y) <= AIM.volleyReach;
    const along = ((now.x - from.x) * lx + (now.y - from.y) * ly) / l;
    const off = Math.abs((now.x - from.x) * ly - (now.y - from.y) * lx) / l;
    return along > 0 && off <= AIM.volleyReach;
  }
  if (Math.hypot(now.x - aimed.x, now.y - aimed.y) <= AIM.volleyReach) return true;
  const a0 = Math.atan2(aimed.y - from.y, aimed.x - from.x);
  const a1 = Math.atan2(now.y - from.y, now.x - from.x);
  let turn = Math.abs(a1 - a0);
  if (turn > Math.PI) turn = 2 * Math.PI - turn;
  return turn <= ((AIM.homingTurnDeg * Math.PI) / 180) * flightS;
}
